/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import css from './SearchFilterWrapper.module.css';

import Slider from 'rc-slider';
import './rc-style.css';
import { Multiselect } from 'multiselect-react-dropdown';
import { languages, services } from '../../../marketplace-custom-config';

import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routeConfiguration';
import Geocoder from '../../../components/LocationAutocompleteInput/GeocoderMapbox';

/* ── small SVG icons rendered inside each pill ── */
function CheckIcon({ on }) {
  return (
    <svg className={css.pillIcon} viewBox="0 0 16 16" fill="none">
      {on ? (
        <>
          <rect x="1" y="1" width="14" height="14" rx="3" fill="rgba(255,255,255,0.25)" stroke="white" strokeWidth="1.5" />
          <path d="M4 8l2.5 2.5L12 5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <rect x="1" y="1" width="14" height="14" rx="3" stroke="#9ca3af" strokeWidth="1.5" />
      )}
    </svg>
  );
}

function RadioIcon({ on }) {
  return (
    <svg className={css.pillIcon} viewBox="0 0 16 16" fill="none">
      {on ? (
        <>
          <circle cx="8" cy="8" r="7" stroke="white" strokeWidth="1.5" />
          <circle cx="8" cy="8" r="3.5" fill="white" />
        </>
      ) : (
        <circle cx="8" cy="8" r="7" stroke="#9ca3af" strokeWidth="1.5" />
      )}
    </svg>
  );
}

/* ── pill button variants ── */
function PillCheck({ label, checked, onClick }) {
  return (
    <button type="button" className={`${css.pill} ${checked ? css.pillOn : ''}`} onClick={onClick}>
      <CheckIcon on={checked} />
      {label}
    </button>
  );
}

function PillRadio({ label, checked, onClick }) {
  return (
    <button type="button" className={`${css.pill} ${checked ? css.pillOn : ''}`} onClick={onClick}>
      <RadioIcon on={checked} />
      {label}
    </button>
  );
}

function BudgetPill({ label, checked, onClick }) {
  return (
    <button type="button" className={`${css.budgetPill} ${checked ? css.pillOn : ''}`} onClick={onClick}>
      <RadioIcon on={checked} />
      {label}
    </button>
  );
}

/* ── section header with extending line ── */
function SectionHead({ label }) {
  return (
    <div className={css.sectionHead}>
      <span className={css.sectionLabel}>{label}</span>
      <span className={css.sectionLine} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════ */

function SearchFilterWrapper(props) {
  const { onHideEvent = () => {}, searchParams } = props;

  const [filters, setFilters] = useState({ category: [] });
  const geocoder = new Geocoder();
  const [predictions, setPredictions] = useState({ list: [] });
  const locationFieldRef = useRef();

  useEffect(() => {
    updateFilterWithParams();
  }, []);

  const updateFilterWithParams = () => {
    let t = {};
    if (searchParams.price) t.priceRange = searchParams.price.split(',').map(Number);
    if (searchParams.pub_category) t.category = searchParams.pub_category.split(',');
    if (searchParams.pub_available) t.available = searchParams.pub_available;
    if (searchParams.pub_per) t.per = searchParams.pub_per;
    if (searchParams['pub_meeting-type']) t.meetingType = searchParams['pub_meeting-type'];
    if (searchParams.pub_language) t.languages = searchParams.pub_language.split(',');
    if (searchParams.pub_location) t.locations = searchParams.pub_location.split(',');
    if (searchParams['pub_type-of-service']) t.services = searchParams['pub_type-of-service'].split(',');
    setFilters(prev => ({ ...prev, ...t }));
  };

  const resetFilter = () => {
    setFilters({ category: [] });
    if (locationFieldRef.current) locationFieldRef.current.value = '';
  };

  const applyFilters = () => {
    let q = {};
    if (filters.category?.length > 0) q.pub_category = filters.category.join(',');
    if (filters.available) q.pub_available = true;
    if (filters.per) q.pub_per = filters.per;
    if (filters.priceRange) q.price = filters.priceRange.join(',');
    if (filters.meetingType && filters.meetingType !== 'Any') q['pub_meeting-type'] = filters.meetingType;
    if (filters.locations) q.pub_location = filters.locations.join(',');
    if (filters.languages) q.pub_language = filters.languages.join(',');
    if (filters.services) q['pub_type-of-service'] = filters.services.join(',');
    if (filters.bounds) q.bounds = filters.bounds;
    if (filters.address) q.address = filters.address;
    props.history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, q));
    onHideEvent();
  };

  const toggleCategory = name => {
    const cats = filters.category || [];
    setFilters({
      ...filters,
      category: cats.includes(name) ? cats.filter(x => x !== name) : [...cats, name],
    });
  };

  const onLocationChange = e => {
    e.persist();
    geocoder
      .getPlacePredictions(e.target.value || '')
      .then(res => {
        setPredictions({ list: res.predictions });
        geocoder
          .getPlaceDetails(res.predictions[0])
          .then(res => {
            setFilters({
              ...filters,
              address: res.address,
              bounds: `${res.bounds.ne.lat},${res.bounds.ne.lng},${res.bounds.sw.lat},${res.bounds.sw.lng}`,
            });
          })
          .catch(err => console.log(err));
      })
      .catch(err => { console.log(err); setPredictions({ list: [] }); });
  };

  const getMultiSelectedValues = name =>
    (filters[name] || []).map(k => ({ key: k, label: k }));

  const dropdownToState = e => e.map(x => x.key);

  const priceMin = filters.priceRange?.[0] ?? 0;
  const priceMax = filters.priceRange?.[1] ?? 5000;

  /* count active filters for the badge */
  const activeCount = [
    (filters.category?.length ?? 0) > 0,
    !!filters.meetingType && filters.meetingType !== 'Any',
    !!filters.per,
    !!filters.priceRange,
    !!filters.address,
    (filters.languages?.length ?? 0) > 0,
    (filters.services?.length ?? 0) > 0,
  ].filter(Boolean).length;

  const dropdownStyle = {
    chips: { background: '#1a3d2f', color: '#fff', borderRadius: '4px', fontSize: '12px' },
    searchBox: { border: 'none', padding: '0', minHeight: 'auto', fontSize: '14px' },
    option: { backgroundColor: 'white', color: '#374151', fontSize: '14px' },
    multiselectContainer: { width: '100%' },
  };

  return (
    <div className={css.wrapper}>

      {/* ── Header ── */}
      <div className={css.header}>
        <span className={css.title}>Filters</span>
        <div className={css.headerRight}>
          {activeCount > 0 && (
            <span className={css.badge}>{activeCount} on</span>
          )}
          <button
            type="button"
            className={css.resetBtn}
            onClick={() => { resetFilter(); props.history.push('/s'); }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* ── ROLE TYPE ── */}
      <div className={css.section}>
        <SectionHead label="ROLE TYPE" />
        <div className={css.pillGrid}>
          {['Coach', 'Speaker', 'Mentor', 'Trainer'].map(role => (
            <PillCheck
              key={role}
              label={role}
              checked={!!(filters.category?.includes(role))}
              onClick={() => toggleCategory(role)}
            />
          ))}
        </div>
      </div>

      {/* ── DELIVERY ── */}
      <div className={css.section}>
        <SectionHead label="DELIVERY" />
        <div className={css.pillGrid}>
          {['Remote', 'In Person', 'Retreat', 'Any'].map(type => (
            <PillRadio
              key={type}
              label={type}
              checked={
                type === 'Any'
                  ? !filters.meetingType || filters.meetingType === 'Any'
                  : filters.meetingType === type
              }
              onClick={() => setFilters({ ...filters, meetingType: type })}
            />
          ))}
        </div>
      </div>

      {/* ── PRICING TYPE ── */}
      <div className={css.section}>
        <SectionHead label="PRICING TYPE" />
        <div className={css.pillGrid}>
          <PillRadio
            label="Package"
            checked={filters.per === 'package'}
            onClick={() => setFilters({ ...filters, per: 'package' })}
          />
          <PillRadio
            label="Hourly"
            checked={filters.per === 'hourly'}
            onClick={() => setFilters({ ...filters, per: 'hourly' })}
          />
        </div>
      </div>

      {/* ── BUDGET ── */}
      <div className={css.section}>
        <SectionHead label="BUDGET" />
        <div className={css.budgetList}>
          <BudgetPill
            label="Less than $500"
            checked={priceMin === 0 && priceMax === 500}
            onClick={() => setFilters({ ...filters, priceRange: [0, 500] })}
          />
          <BudgetPill
            label="Less than $1,000"
            checked={priceMin === 0 && priceMax === 1000}
            onClick={() => setFilters({ ...filters, priceRange: [0, 1000] })}
          />
          <BudgetPill
            label="More than $1,000"
            checked={priceMin === 1000 && priceMax === 5000}
            onClick={() => setFilters({ ...filters, priceRange: [1000, 5000] })}
          />
        </div>
        <div className={css.priceRow}>
          <input
            className={css.priceInput}
            type="number"
            value={priceMin}
            min={0}
            max={priceMax}
            onChange={e => setFilters({ ...filters, priceRange: [Number(e.target.value), priceMax] })}
          />
          <span className={css.priceDash}>—</span>
          <input
            className={css.priceInput}
            type="number"
            value={priceMax}
            min={priceMin}
            max={5000}
            onChange={e => setFilters({ ...filters, priceRange: [priceMin, Number(e.target.value)] })}
          />
        </div>
        <div className={css.sliderWrap}>
          <Slider.Range
            min={0}
            max={5000}
            step={10}
            value={[priceMin, priceMax]}
            onChange={value => setFilters({ ...filters, priceRange: value })}
            trackStyle={[{ backgroundColor: '#0d9488', height: 6 }]}
            handleStyle={[
              { borderColor: '#0d9488', backgroundColor: '#0d9488', width: 14, height: 14, marginTop: -4 },
              { borderColor: '#0d9488', backgroundColor: '#0d9488', width: 14, height: 14, marginTop: -4 },
            ]}
            railStyle={{ backgroundColor: '#e5e7eb', height: 6 }}
          />
        </div>
      </div>

      {/* ── LOCATION ── */}
      <div className={css.section}>
        <SectionHead label="LOCATION" />
        <input
          ref={locationFieldRef}
          list="searchResultsSidebar"
          onChange={onLocationChange}
          className={css.textInput}
          type="text"
          placeholder="City, country..."
        />
        <datalist id="searchResultsSidebar">
          {predictions.list.map((item, i) => (
            <option key={i}>{item.place_name_en}</option>
          ))}
        </datalist>
      </div>

      {/* ── LANGUAGE ── */}
      <div className={css.section}>
        <SectionHead label="LANGUAGE" />
        <div className={css.dropdownWrap}>
          <Multiselect
            onRemove={e => setFilters({ ...filters, languages: dropdownToState(e) })}
            onSelect={e => setFilters({ ...filters, languages: dropdownToState(e) })}
            style={dropdownStyle}
            options={languages}
            displayValue="label"
            selectedValues={getMultiSelectedValues('languages')}
            placeholder="Any language"
          />
          <svg className={css.dropdownChevron} viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* ── SERVICE TYPE ── */}
      <div className={css.section}>
        <SectionHead label="SERVICE TYPE" />
        <div className={css.dropdownWrap}>
          <Multiselect
            onRemove={e => setFilters({ ...filters, services: dropdownToState(e) })}
            onSelect={e => setFilters({ ...filters, services: dropdownToState(e) })}
            style={dropdownStyle}
            options={services}
            displayValue="label"
            selectedValues={getMultiSelectedValues('services')}
            placeholder="Any service"
          />
          <svg className={css.dropdownChevron} viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* ── Apply ── */}
      <button className={css.applyBtn} onClick={applyFilters}>
        Apply filters
      </button>

    </div>
  );
}

export default withRouter(SearchFilterWrapper);
